import React, { useState, useEffect } from 'react';
import { Trash2, Upload, Download, FileText, Eye, EyeOff, Shield } from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
// Mammoth wordt gebruikt voor DOCX bestanden maar is niet beschikbaar
// We zullen een alternatieve aanpak gebruiken voor documenten

const DocumentAnonymizer = () => {
  const [files, setFiles] = useState([]);
  const [anonymizedFiles, setAnonymizedFiles] = useState([]);
  const [pseudonymMap, setPseudonymMap] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [showOriginal, setShowOriginal] = useState(false);
  const [sensitivePatterns, setSensitivePatterns] = useState([
    { type: 'Naam', pattern: /\b[A-Z][a-z]+ [A-Z][a-z]+\b/g, prefix: 'PERSOON_' },
    { type: 'E-mail', pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, prefix: 'EMAIL_' },
    { type: 'Telefoonnummer', pattern: /\b(\+[0-9]{2}|0)[0-9]{9}\b/g, prefix: 'TEL_' },
    { type: 'BSN', pattern: /\b[0-9]{9}\b/g, prefix: 'BSN_' },
    { type: 'Postcode', pattern: /\b[1-9][0-9]{3} ?[A-Z]{2}\b/g, prefix: 'POSTCODE_' },
    { type: 'IBAN', pattern: /\bNL[0-9]{2}[A-Z]{4}[0-9]{10}\b/g, prefix: 'IBAN_' },
    { type: 'Bedrijfsnaam', pattern: /\b[A-Z][A-Za-z0-9]+ (B\.V\.|N\.V\.|Inc\.|Ltd\.)\b/g, prefix: 'BEDRIJF_' }
  ]);
  const [customPatterns, setCustomPatterns] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Functie om een uniek pseudoniem te genereren voor elke gevonden match
  const generatePseudonym = (value, type) => {
    if (pseudonymMap[value]) {
      return pseudonymMap[value];
    }
    
    const pattern = sensitivePatterns.find(p => p.type === type) || 
                    customPatterns.find(p => p.type === type);
    
    if (!pattern) return value;
    
    const prefix = pattern.prefix || 'ANON_';
    const randomId = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const newPseudonym = `${prefix}${randomId}`;
    
    // Update de pseudoniem mapping
    setPseudonymMap(prev => ({
      ...prev,
      [value]: newPseudonym
    }));
    
    return newPseudonym;
  };

  // Document verwerking
  const processDocument = async (file) => {
    setIsProcessing(true);
    try {
      let content = '';
      let anonymizedContent = '';
      
      // Lees bestandsinhoud op basis van bestandstype
      if (file.type === 'text/plain' || file.type === 'text/csv') {
        content = await readFileAsText(file);
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        // Omdat mammoth niet beschikbaar is, informeren we de gebruiker
        alert('DOCX bestanden kunnen niet direct worden verwerkt. Converteer naar TXT of CSV.');
        content = `[DOCX bestand: ${file.name}] - Kan niet worden verwerkt zonder mammoth bibliotheek.`;
      } else if (file.type.includes('spreadsheet') || file.type.includes('excel')) {
        // Excel bestanden
        const arrayBuffer = await readFileAsArrayBuffer(file);
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        content = XLSX.utils.sheet_to_csv(firstSheet);
      } else {
        throw new Error('Niet-ondersteund bestandstype');
      }
      
      // Pas anonimisering toe op de inhoud
      anonymizedContent = anonymizeContent(content);
      
      // Voeg het geanonimiseerde bestand toe aan de lijst
      setAnonymizedFiles(prev => [...prev, {
        name: `anon_${file.name}`,
        content: anonymizedContent,
        originalName: file.name
      }]);
      
      // Toon de preview van het geselecteerde bestand
      if (files.length === 1) {
        setSelectedFile({
          original: content,
          anonymized: anonymizedContent,
          name: file.name
        });
        setPreview(anonymizedContent);
      }
    } catch (error) {
      console.error('Fout bij verwerken document:', error);
      alert(`Fout bij verwerken van ${file.name}: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const anonymizeContent = (content) => {
    let anonymized = content;
    
    // Pas alle gedefinieerde patronen toe
    [...sensitivePatterns, ...customPatterns].forEach(({ type, pattern }) => {
      anonymized = anonymized.replace(pattern, (match) => {
        return generatePseudonym(match, type);
      });
    });
    
    return anonymized;
  };

  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  const readFileAsArrayBuffer = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsArrayBuffer(file);
    });
  };

  // Verwerkt bestanden als ze zijn geselecteerd
  useEffect(() => {
    if (files.length > 0) {
      files.forEach(file => {
        processDocument(file);
      });
    }
  }, [files]);

  // Bestandsbeheer functies
  const handleFileUpload = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setAnonymizedFiles(prev => prev.filter((_, i) => i !== index));
    
    if (selectedFile && selectedFile.name === files[index].name) {
      setSelectedFile(null);
      setPreview('');
    }
  };

  const downloadAnonymizedFile = (index) => {
    const file = anonymizedFiles[index];
    const blob = new Blob([file.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const viewFile = (index) => {
    const anonymizedFile = anonymizedFiles[index];
    const originalFile = files[index];
    
    setSelectedFile({
      original: originalFile ? originalFile.text : '',
      anonymized: anonymizedFile.content,
      name: anonymizedFile.originalName
    });
    
    setPreview(showOriginal ? originalFile.text : anonymizedFile.content);
  };

  const toggleView = () => {
    setShowOriginal(!showOriginal);
    if (selectedFile) {
      setPreview(showOriginal ? selectedFile.anonymized : selectedFile.original);
    }
  };

  // Voeg een aangepast patroon toe
  const addCustomPattern = () => {
    setCustomPatterns(prev => [...prev, {
      type: 'Aangepast',
      pattern: '',
      prefix: 'CUSTOM_'
    }]);
  };

  const updateCustomPattern = (index, field, value) => {
    setCustomPatterns(prev => 
      prev.map((p, i) => 
        i === index ? { ...p, [field]: field === 'pattern' ? new RegExp(value, 'g') : value } : p
      )
    );
  };

  const removeCustomPattern = (index) => {
    setCustomPatterns(prev => prev.filter((_, i) => i !== index));
  };

  // Download pseudoniem mapping
  const downloadPseudonymMap = () => {
    const mappingText = Object.entries(pseudonymMap)
      .map(([original, pseudonym]) => `${original},${pseudonym}`)
      .join('\n');
    
    const blob = new Blob([mappingText], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pseudonym_mapping.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-blue-600 text-white p-4">
        <div className="container mx-auto">
          <h1 className="text-2xl font-bold flex items-center">
            <Shield className="mr-2" /> Document Anonimisator
          </h1>
          <p className="text-blue-100">Bescherm persoonlijke gegevens door documenten te anonimiseren</p>
        </div>
      </header>
      
      <main className="container mx-auto p-4 flex-grow">
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-xl font-semibold mb-4">Bestanden uploaden</h2>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-4">
            <input
              type="file"
              id="file-upload"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <Upload className="mx-auto mb-2 text-blue-500" size={32} />
              <p className="text-gray-700 mb-1">Klik om bestanden te uploaden</p>
              <p className="text-gray-500 text-sm">Ondersteunde formaten: TXT, CSV, DOCX, XLSX</p>
            </label>
          </div>
          
          {files.length > 0 && (
            <div className="mt-4">
              <h3 className="font-medium mb-2">Geüploade bestanden ({files.length})</h3>
              <ul className="divide-y divide-gray-200">
                {files.map((file, index) => (
                  <li key={index} className="py-2 flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="text-gray-500 mr-2" size={18} />
                      <span>{file.name}</span>
                    </div>
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => viewFile(index)} 
                        className="p-1 text-blue-600 hover:text-blue-800"
                        title="Bekijk bestand"
                      >
                        <Eye size={18} />
                      </button>
                      <button 
                        onClick={() => downloadAnonymizedFile(index)} 
                        className="p-1 text-green-600 hover:text-green-800"
                        title="Download geanonimiseerd bestand"
                        disabled={!anonymizedFiles[index]}
                      >
                        <Download size={18} />
                      </button>
                      <button 
                        onClick={() => removeFile(index)} 
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Verwijder bestand"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Anonimisatie Instellingen</h2>
            
            <div className="mb-4">
              <h3 className="font-medium mb-2">Standaard Patronen</h3>
              <ul className="space-y-2">
                {sensitivePatterns.map((pattern, index) => (
                  <li key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="font-medium">{pattern.type}</span>
                    <span className="text-sm text-gray-500">{pattern.prefix}XXXX</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium">Aangepaste Patronen</h3>
                <button 
                  onClick={addCustomPattern} 
                  className="text-sm bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                >
                  + Nieuw patroon
                </button>
              </div>
              
              {customPatterns.length > 0 ? (
                <ul className="space-y-3">
                  {customPatterns.map((pattern, index) => (
                    <li key={index} className="bg-gray-50 p-3 rounded">
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <input
                          type="text"
                          placeholder="Type (bijv. Klantnummer)"
                          value={pattern.type}
                          onChange={(e) => updateCustomPattern(index, 'type', e.target.value)}
                          className="border rounded p-1 text-sm col-span-2"
                        />
                        <input
                          type="text"
                          placeholder="Prefix (bijv. KLANT_)"
                          value={pattern.prefix}
                          onChange={(e) => updateCustomPattern(index, 'prefix', e.target.value)}
                          className="border rounded p-1 text-sm"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="text"
                          placeholder="Regex patroon"
                          onChange={(e) => updateCustomPattern(index, 'pattern', e.target.value)}
                          className="border rounded p-1 text-sm flex-grow mr-2"
                        />
                        <button 
                          onClick={() => removeCustomPattern(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 italic">Geen aangepaste patronen gedefinieerd</p>
              )}
            </div>
            
            <div className="mt-6">
              <button 
                onClick={downloadPseudonymMap}
                disabled={Object.keys(pseudonymMap).length === 0}
                className={`w-full py-2 rounded font-medium ${
                  Object.keys(pseudonymMap).length === 0 
                    ? 'bg-gray-300 text-gray-500' 
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                Download Pseudoniem Mapping
              </button>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Bestandsvoorbeeld</h2>
              {selectedFile && (
                <button 
                  onClick={toggleView}
                  className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  {showOriginal ? <EyeOff size={16} className="mr-1" /> : <Eye size={16} className="mr-1" />}
                  {showOriginal ? 'Toon Anoniem' : 'Toon Origineel'}
                </button>
              )}
            </div>
            
            {selectedFile ? (
              <div className="bg-gray-50 p-4 rounded max-h-96 overflow-auto">
                <h3 className="text-sm font-medium mb-2 text-gray-500">
                  {selectedFile.name} {showOriginal ? '(Origineel)' : '(Geanonimiseerd)'}
                </h3>
                <pre className="text-sm whitespace-pre-wrap">{preview}</pre>
              </div>
            ) : (
              <div className="bg-gray-50 p-8 rounded text-center text-gray-500">
                <FileText className="mx-auto mb-2" size={32} />
                <p>Selecteer een bestand om de inhoud te bekijken</p>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <footer className="bg-gray-800 text-white p-4 mt-8">
        <div className="container mx-auto text-center text-sm">
          <p>Document Anonimisator Tool &copy; 2025 - Alle rechten voorbehouden</p>
          <p className="text-gray-400 mt-1">Bescherm uw gevoelige gegevens met geavanceerde anonimisatietechnieken</p>
        </div>
      </footer>
    </div>
  );
};

export default DocumentAnonymizer;